import { useCallback, useEffect, useState } from 'react';
import { useStore } from '../state/store';
import {
  orgListUsers,
  orgCreateUser,
  orgUpdateUser,
  orgDeleteUser,
  orgRename,
  CloudError,
  type OrgInfo,
  type OrgMemberRow,
} from '../lib/cloud';
import { IconPlus, IconTrash } from './icons';

function fmtSize(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
}

function fmtDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString();
}

const ROLE_LABELS: Record<string, string> = {
  main_admin: 'SPadministrator',
  org_admin: 'Administrator (org)',
  org_manager: 'Manager (org)',
  member: 'Member (org)',
};

/** Member management for organization administrators. */
export function OrgView() {
  const setView = useStore((s) => s.setView);
  const pushLine = useStore((s) => s.pushLine);
  const me = useStore((s) => s.auth.user);

  const [org, setOrg] = useState<OrgInfo | null>(null);
  const [members, setMembers] = useState<OrgMemberRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setPassword] = useState('');
  const [newRole, setNewRole] = useState<'org_admin' | 'org_manager' | 'member'>('member');
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkCreated, setBulkCreated] = useState<{ username: string; password: string }[] | null>(null);
  const [resetMember, setResetMember] = useState<OrgMemberRow | null>(null);
  const [resetPassword, setResetPassword] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetDone, setResetDone] = useState(false);
  const setOrgName = useStore((s) => s.setOrgName);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameText, setRenameText] = useState('');
  const [renameBusy, setRenameBusy] = useState(false);
  const [renameDone, setRenameDone] = useState(false);
  const [bulkPasswordMode, setBulkPasswordMode] = useState<'none' | 'random4' | 'fixed'>('none');
  const [bulkFixedPassword, setBulkFixedPassword] = useState('');

  const refresh = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const data = await orgListUsers();
      setOrg(data.org);
      setMembers(data.users);
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not load your organization.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function createMember(): Promise<void> {
    setError(null);
    setCreating(true);
    try {
      const { user } = await orgCreateUser(newUsername.trim(), newPassword, newRole);
      pushLine('ok', `Created ${ROLE_LABELS[user.role] ?? user.role} "${user.username}".`);
      setNewUsername('');
      setPassword('');
      setNewRole('member');
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not create the member.');
    } finally {
      setCreating(false);
    }
  }

  async function changeRole(member: OrgMemberRow, role: string): Promise<void> {
    setError(null);
    try {
      await orgUpdateUser(member.id, { role: role as OrgMemberRow['role'] });
      pushLine('ok', `"${member.username}" is now ${ROLE_LABELS[role] ?? role}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not change the role.');
      void refresh();
    }
  }

  /** Accepts one username per line (commas also separate). No passwords. */
  function parseBulk(text: string): string[] {
    return text
      .split(/[\n,]+/)
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
  }

  function random4Digit(): string {
    return String(1000 + Math.floor(Math.random() * 9000));
  }

  function bulkPassword(): string {
    if (bulkPasswordMode === 'random4') return random4Digit();
    if (bulkPasswordMode === 'fixed') return bulkFixedPassword;
    return '';
  }

  async function runBulkCreate(): Promise<void> {
    setBulkError(null);
    const usernames = parseBulk(bulkText);
    if (usernames.length === 0) {
      setBulkError('Enter at least one username (one per line).');
      return;
    }
    if (bulkPasswordMode === 'fixed' && bulkFixedPassword.length < 4) {
      setBulkError('The shared password must be at least 4 characters.');
      return;
    }
    setBulkBusy(true);
    const created: { username: string; password: string }[] = [];
    const failed: string[] = [];
    for (const username of usernames) {
      const password = bulkPassword();
      try {
        await orgCreateUser(username, password);
        created.push({ username, password });
      } catch {
        failed.push(username);
      }
    }
    setBulkBusy(false);
    void refresh();
    if (created.length > 0) {
      setBulkCreated(created);
      setBulkText('');
      if (failed.length > 0) setBulkError(`Created ${created.length}, failed: ${failed.join(', ')} (already taken or invalid).`);
    } else {
      setBulkError(`None created — taken or invalid: ${failed.join(', ')}.`);
    }
  }

  async function toggleDisabled(member: OrgMemberRow): Promise<void> {
    setError(null);
    try {
      await orgUpdateUser(member.id, { disabled: !member.disabled });
      pushLine('info', `${member.username} is now ${member.disabled ? 'active' : 'disabled'}.`);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Update failed.');
    }
  }

  async function deleteMember(member: OrgMemberRow): Promise<void> {
    setError(null);
    try {
      await orgDeleteUser(member.id);
      pushLine('info', `Deleted ${member.username} and their projects.`);
      setConfirmDelete(null);
      void refresh();
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Delete failed.');
    }
  }

  function randomPassword(): string {
    const chars = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789';
    let out = '';
    const buf = crypto.getRandomValues(new Uint32Array(8));
    for (let i = 0; i < buf.length; i++) out += chars[buf[i]! % chars.length];
    return out;
  }

  async function saveReset(): Promise<void> {
    if (!resetMember) return;
    setError(null);
    setResetBusy(true);
    try {
      await orgUpdateUser(resetMember.id, { password: resetPassword });
      pushLine('ok', `Password reset for "${resetMember.username}".`);
      setResetDone(true);
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not reset the password.');
    } finally {
      setResetBusy(false);
    }
  }

  function openReset(member: OrgMemberRow): void {
    setResetMember(member);
    setResetPassword(randomPassword());
    setResetDone(false);
    setError(null);
  }

  async function saveRename(): Promise<void> {
    setError(null);
    setRenameBusy(true);
    try {
      const { name } = await orgRename(renameText.trim());
      setOrgName(name);
      setRenameDone(true);
      pushLine('ok', `The organization was renamed to "${name}".`);
    } catch (err) {
      setError(err instanceof CloudError ? err.message : 'Could not rename the organization.');
    } finally {
      setRenameBusy(false);
    }
  }

  const usedBytes = (members ?? []).reduce((sum, m) => sum + m.usedBytes, 0);

  return (
    <div className="admin-shell">
      <div className="admin-head">
        <span className="admin-title">Organization{org ? ` — ${org.name}` : ''}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 10 }}>
          {me?.role === 'org_admin' && (
            <button
              className="btn"
              onClick={() => { setRenameText(org?.name ?? ''); setRenameDone(false); setError(null); setRenameOpen(true); }}
              disabled={!org}
            >
              Rename
            </button>
          )}
          <button className="btn" onClick={() => setView('home')}>
            Back to projects
          </button>
        </div>
      </div>

      <div className="admin-body">
        {error && (
          <div style={{ maxWidth: 980, margin: '0 auto 12px' }}>
            <div className="form-error err">{error}</div>
          </div>
        )}

        <div className="admin-card" style={{ marginBottom: 20 }}>
          <div className="admin-card-head">
            <span className="admin-card-title">Create member</span>
            <button
              className="btn btn-sm"
              onClick={() => { setBulkOpen(true); setBulkError(null); setBulkCreated(null); }}
              title="Create many members at once from a username,password list"
            >
              <IconPlus width={13} height={13} />
              Create multiple
            </button>
          </div>
          <div style={{ padding: '14px 18px' }}>
            <div className="field-row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                <div className="field-label"><span>Username</span></div>
                <input
                  type="text"
                  value={newUsername}
                  placeholder="letters, digits, . _ -"
                  spellCheck={false}
                  onChange={(e) => setNewUsername(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: '1 1 180px', marginBottom: 0 }}>
                <div className="field-label">
                  <span>Password</span>
                </div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
              <div className="field" style={{ flex: '0 1 170px', marginBottom: 0 }}>
                <div className="field-label"><span>Role</span></div>
                <select value={newRole} onChange={(e) => setNewRole(e.target.value as typeof newRole)}>
                  {me?.role === 'org_admin' && <option value="org_admin">Administrator</option>}
                  {me?.role === 'org_admin' && <option value="org_manager">Manager</option>}
                  <option value="member">Member</option>
                </select>
              </div>
              <button
                className="btn btn-primary"
                style={{ height: 38 }}
                onClick={() => void createMember()}
                disabled={creating || !newUsername.trim()}
              >
                <IconPlus width={14} height={14} />
                {creating ? 'Creating…' : 'Create user under this org'}
              </button>
            </div>
          </div>
        </div>

        <div className="admin-card">
          <div className="admin-card-head">
            <span className="admin-card-title">Members</span>
            {org && (
              <span className="mono">
                {(members ?? []).length} users · unlimited · {fmtSize(usedBytes)} / {org.quotaMb} MB storage
              </span>
            )}
          </div>

          {members === null && !error && <div className="admin-empty">Loading…</div>}

          {members !== null && members.length === 0 && !error && (
            <div className="admin-empty">No members yet — create the first one above.</div>
          )}

          {members !== null && members.length > 0 && (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Projects</th>
                  <th>Storage used</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id}>
                    <td style={{ fontWeight: 600 }}>
                      {m.username}
                      {me?.id === m.id && <span className="badge info" style={{ marginLeft: 8 }}>you</span>}
                    </td>
                    <td>
                      {me?.role === 'org_admin' && me.id !== m.id ? (
                        <select
                          value={m.role}
                          onChange={(e) => void changeRole(m, e.target.value)}
                          title="Change this member's role"
                        >
                          <option value="org_admin">Administrator</option>
                          <option value="org_manager">Manager</option>
                          <option value="member">Member</option>
                        </select>
                      ) : (
                        <span className={m.role === 'member' ? 'mono' : 'badge info'}>{ROLE_LABELS[m.role] ?? m.role}</span>
                      )}
                    </td>
                    <td className="mono">{m.projectCount}</td>
                    <td className="mono">{fmtSize(m.usedBytes)}</td>
                    <td>
                      {m.disabled
                        ? <span className="badge err">disabled</span>
                        : <span className="badge ok">active</span>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm" onClick={() => openReset(m)} title="Set a new password for this member">
                        Reset password
                      </button>{' '}
                      <button className="btn btn-sm" onClick={() => void toggleDisabled(m)}>
                        {m.disabled ? 'Enable' : 'Disable'}
                      </button>{' '}
                      {confirmDelete === m.id ? (
                        <button className="btn btn-sm btn-danger" onClick={() => void deleteMember(m)}>
                          Confirm delete
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => setConfirmDelete(m.id)}
                          disabled={me?.id === m.id}
                          title={me?.id === m.id ? 'You cannot delete your own account' : 'Delete member and their projects'}
                        >
                          <IconTrash width={13} height={13} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {bulkOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Create multiple members">
          <div className="modal is-wide">
            <div className="modal-head">
              <div className="modal-title">Create multiple members</div>
              <button className="modal-x" aria-label="Close" onClick={() => setBulkOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {bulkCreated === null ? (
                <>
                  <div className="field">
                    <div className="field-label"><span>Usernames</span></div>
                    <textarea
                      className="bulk-input"
                      rows={8}
                      value={bulkText}
                      placeholder={'username1\nusername2\nusername3'}
                      spellCheck={false}
                      onChange={(e) => setBulkText(e.target.value)}
                    />
                  </div>
                  <p className="field-note" style={{ marginTop: 0 }}>
                    One username per line. By default no password is set — each person picks their own
                    at first sign-in.
                  </p>
                  <div className="field">
                    <div className="field-label"><span>Passwords</span></div>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <input
                        type="radio"
                        name="bulk-password"
                        checked={bulkPasswordMode === 'none'}
                        onChange={() => setBulkPasswordMode('none')}
                      />
                      No password — users create their own at first sign-in
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
                      <input
                        type="radio"
                        name="bulk-password"
                        checked={bulkPasswordMode === 'random4'}
                        onChange={() => setBulkPasswordMode('random4')}
                      />
                      Set a random 4-digit number as the password (unique per user)
                    </label>
                    <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                      <input
                        type="radio"
                        name="bulk-password"
                        checked={bulkPasswordMode === 'fixed'}
                        onChange={() => setBulkPasswordMode('fixed')}
                      />
                      <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        Set all passwords to:
                        <input
                          type="text"
                          value={bulkFixedPassword}
                          onChange={(e) => setBulkFixedPassword(e.target.value)}
                          disabled={bulkPasswordMode !== 'fixed'}
                          style={{ width: 160 }}
                          spellCheck={false}
                        />
                      </span>
                    </label>
                  </div>
                  {bulkError && <div className="form-error err">{bulkError}</div>}
                </>
              ) : (
                <>
                  <p className="field-note" style={{ marginTop: 0 }}>
                    Created {bulkCreated.length} member{bulkCreated.length === 1 ? '' : 's'}. Print the
                    credentials and cut along the dashed lines — one card per person.
                  </p>
                  <div className="print-area" id="credential-cards">
                    <div className="print-header">
                      <strong>{org?.name ?? 'SPblock'}</strong> — SPblock sign-in credentials
                    </div>
                    <div className="credential-grid">
                      {bulkCreated.map((c) => (
                        <div className="credential-card" key={c.username}>
                          <div className="credential-org">{org?.name ?? 'SPblock'}</div>
                          <div className="credential-row"><span>Website</span><strong>spblock.pages.dev</strong></div>
                          <div className="credential-row"><span>Organization</span><strong>{org?.name ?? ''}</strong></div>
                          <div className="credential-row"><span>Username</span><strong className="mono">{c.username}</strong></div>
                          <div className="credential-row"><span>Password</span><strong className="mono">{c.password || '(choose at first sign-in)'}</strong></div>
                        </div>
                      ))}
                    </div>
                  </div>
                  {bulkError && <div className="form-error err">{bulkError}</div>}
                </>
              )}
            </div>
            <div className="modal-foot">
              {bulkCreated === null ? (
                <>
                  <button className="btn" onClick={() => setBulkOpen(false)}>Cancel</button>
                  <button className="btn btn-primary" onClick={() => void runBulkCreate()} disabled={bulkBusy || !bulkText.trim()}>
                    {bulkBusy ? 'Creating…' : `Create ${parseBulk(bulkText).length || ''} member${parseBulk(bulkText).length === 1 ? '' : 's'}`}
                  </button>
                </>
              ) : (
                <>
                  <button className="btn" onClick={() => { setBulkCreated(null); setBulkError(null); }}>Create more</button>
                  <button className="btn btn-primary" onClick={() => window.print()}>Print credentials</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {resetMember && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Reset password">
          <div className="modal">
            <div className="modal-head">
              <div className="modal-title">Reset password — {resetMember.username}</div>
              <button className="modal-x" aria-label="Close" onClick={() => setResetMember(null)}>✕</button>
            </div>
            <div className="modal-body">
              {resetDone ? (
                <p className="field-note" style={{ marginTop: 0 }}>
                  The new password is set. Give it to <strong>{resetMember.username}</strong> — their
                  other signed-in devices have been signed out.
                </p>
              ) : (
                <>
                  <div className="field">
                    <div className="field-label"><span>New password</span></div>
                    <div className="field-row">
                      <input
                        type="text"
                        value={resetPassword}
                        onChange={(e) => setResetPassword(e.target.value)}
                        spellCheck={false}
                        style={{ flex: 1 }}
                      />
                      <button className="btn" onClick={() => setResetPassword(randomPassword())}>Generate</button>
                    </div>
                  </div>
                  {error && <div className="form-error err">{error}</div>}
                </>
              )}
            </div>
            <div className="modal-foot">
              {resetDone ? (
                <button className="btn btn-primary" onClick={() => setResetMember(null)}>Done</button>
              ) : (
                <>
                  <button className="btn" onClick={() => setResetMember(null)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={() => void saveReset()}
                    disabled={resetBusy || resetPassword.length < 4}
                  >
                    {resetBusy ? 'Saving…' : 'Set password'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {renameOpen && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Rename organization">
          <div className="modal">
            <div className="modal-head">
              <div className="modal-title">Rename organization</div>
              <button className="modal-x" aria-label="Close" onClick={() => setRenameOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              {renameDone ? (
                <p className="field-note" style={{ marginTop: 0 }}>
                  The organization is now called <strong>{renameText.trim()}</strong>.
                </p>
              ) : (
                <>
                  <div className="field">
                    <div className="field-label"><span>New name</span></div>
                    <input
                      type="text"
                      value={renameText}
                      spellCheck={false}
                      style={{ width: '100%' }}
                      onChange={(e) => setRenameText(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') void saveRename(); }}
                    />
                  </div>
                  {error && <div className="form-error err">{error}</div>}
                </>
              )}
            </div>
            <div className="modal-foot">
              {renameDone ? (
                <button className="btn btn-primary" onClick={() => setRenameOpen(false)}>Done</button>
              ) : (
                <>
                  <button className="btn" onClick={() => setRenameOpen(false)}>Cancel</button>
                  <button
                    className="btn btn-primary"
                    onClick={() => void saveRename()}
                    disabled={renameBusy || renameText.trim().length < 2}
                  >
                    {renameBusy ? 'Saving…' : 'Rename'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
